import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';

interface Expense extends Record<string, string | number> {
  category: string;
}

export function useExpensesEdit() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [months, setMonths] = useState<string[]>([]);
  const [originalData, setOriginalData] = useState<Record<string, Expense>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [changesMade, setChangesMade] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const csrfToken = await getCsrfToken();

        const response = await fetch('/api/expenses/edit/', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'X-CSRFToken': csrfToken,
          }
        });

        if (response.redirected) {
          window.location.href = '/login';
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch expenses data');
        }
        const data = await response.json();
        if (data.success) {
          setExpenses(data.data.expenses_table);
          setMonths(data.data.months);
          setOriginalData(createOriginalData(data.data.expenses_table));
        }
      } catch (error) {
        console.error('Error fetching expenses:', error);
      }
    };

    fetchExpenses();
  }, []);

  const createOriginalData = (expensesData: Expense[]) => {
    const data: Record<string, Expense> = {};
    expensesData.forEach(row => {
      if (row.category !== 'Total') {
        data[row.category] = { ...row };
      }
    });
    return data;
  };

  const handleCellChange = (index: number, month: string, value: string) => {
    const numericValue = parseFloat(value.replace(/,/g, '')) || 0;
    const updatedExpenses = [...expenses];
    updatedExpenses[index] = { ...updatedExpenses[index], [month]: numericValue };
    setExpenses(updatedExpenses);
    checkForChanges(updatedExpenses);
    updateTotals(updatedExpenses);
  };

  const handleCategoryChange = (index: number, value: string) => {
    const updatedExpenses = [...expenses];
    updatedExpenses[index].category = value;
    setExpenses(updatedExpenses);
    checkForChanges(updatedExpenses);
  };

  const checkForChanges = (currentExpenses: Expense[]) => {
    let changes = false;
    
    Object.keys(originalData).forEach(originalCategory => {
      if (!currentExpenses.some(row => row.category === originalCategory)) {
        changes = true;
      }
    });

    currentExpenses.forEach(row => {
      if (row.category !== 'Total') {
        if (!(row.category in originalData)) {
          changes = true;
        } else {
          months.forEach(month => {
            if (row[month] !== originalData[row.category]?.[month]) {
              changes = true;
            }
          });
        }
      }
    });
    
    setChangesMade(changes);
  };

  const updateTotals = (currentExpenses: Expense[]) => {
    const expensesWithoutTotal = currentExpenses.filter(row => row.category !== 'Total');
    const totalRow: Expense = { category: 'Total' };
    
    months.forEach((month: string) => {
      const sum = expensesWithoutTotal.reduce((acc: number, row: Expense) => {
        const value = parseFloat(String(row[month])) || 0;
        return acc + value;
      }, 0);
      totalRow[month] = sum.toFixed(2);
    });

    const finalExpenses = [...expensesWithoutTotal, totalRow];
    setExpenses(finalExpenses);
  };

  const handleSaveChanges = async () => {
    try {
      const updatedData = expenses.filter(row => row.category !== 'Total').map(row => ({
        old_category: originalData[row.category] ? row.category : '',
        new_category: row.category,
        ...Object.fromEntries(months.map(month => [month, row[month]]))
      }));

      const csrfToken = await getCsrfToken();
      
      const response = await fetch('/api/update_expenses/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
      }

      const data = await response.json();
      if (data.success) {
        alert('Changes saved successfully!');
        navigate('/expenses/dashboard');
      }
    } catch (error: unknown) {
      console.error('Error saving changes:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      alert(`Error saving changes: ${errorMessage}`);
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !expenses.some(row => row.category.toLowerCase() === newCategory.trim().toLowerCase())) {
      const newRow = {
        category: newCategory.trim(),
        ...Object.fromEntries(months.map(month => [month, '0']))
      };
      const totalRow = expenses.find(row => row.category === 'Total') || { category: 'Total' };
      setExpenses([...expenses.filter(row => row.category !== 'Total'), newRow, totalRow]);
      setNewCategory('');
      setIsModalOpen(false);
      checkForChanges([...expenses, newRow]);
      updateTotals([...expenses, newRow]);
    } else {
      alert('Please enter a unique category name.');
    }
  };

  const handleRemoveCategory = (categoryToRemove: string) => {
    if (categoryToRemove === 'Total') return;
    
    if (window.confirm(`Are you sure you want to remove the category "${categoryToRemove}"?`)) {
      const updatedExpenses = expenses.filter(row => row.category !== categoryToRemove);
      setExpenses(updatedExpenses);
      checkForChanges(updatedExpenses);
      updateTotals(updatedExpenses);
    }
  };

  return {
    expenses,
    months,
    isModalOpen,
    newCategory,
    changesMade,
    handleCellChange,
    handleCategoryChange,
    handleSaveChanges,
    handleAddCategory,
    handleRemoveCategory,
    setIsModalOpen,
    setNewCategory
  };
}